import { useCallback, useMemo } from 'react';

import {
  useErpCourseProgramLessonsQuery,
  useSetCourseProgramMutation,
} from 'core/graphql';

import { ICourseProgramLesson } from './careerModel';

const EMPTY_LESSONS: ICourseProgramLesson[] = [];

/**
 * ТЗ 0040 — занятия программы курса.
 *
 * Нужны, чтобы «после занятия №N» выбиралось по НАЗВАНИЮ. Программа может
 * быть не заполнена вовсе (её грузят Excel-импортом из Django-админки) —
 * тогда список пуст, и экран обязан сказать об этом прямо, а не показать
 * пустой выпадающий список, который читается как поломка.
 */
export const useCourseProgram = (courseId: string | null) => {
  const { data, loading, refetch } = useErpCourseProgramLessonsQuery({
    variables: { courseId: courseId as string },
    skip: !courseId,
    fetchPolicy: 'cache-and-network',
  });

  const lessons: ICourseProgramLesson[] =
    data?.courseProgramLessons ?? EMPTY_LESSONS;

  return {
    lessons,
    // ТЗ 0040 доводка: рама курса. `exists=false` — программы нет вовсе, и
    // экран обязан сказать это словами, а не показать три пустых поля.
    frame: data?.courseProgramFrame ?? null,
    loading,
    refetch,
    /** Название занятия по номеру — для подписи уже сохранённой привязки. */
    lessonName: useMemo(() => {
      const map = new Map(lessons.map((row) => [row.number, row.name]));
      return (number: number | null) =>
        number ? map.get(number) ?? null : null;
    }, [lessons]),
  };
};

/**
 * ТЗ 0040 фаза C — правка программы курса.
 *
 * 🔴 `lessonsRemoved` возвращается наверх намеренно: вместе с занятием уходят
 * его материалы, и экран обязан сказать об этом вслух. Занятие, отправленное
 * БЕЗ `id`, сервер считает новым — то есть удаляет старое на его месте.
 */
export const useSetCourseProgram = () => {
  const [save, { loading }] = useSetCourseProgramMutation();

  const setProgram = useCallback(
    async (courseId: string, modules: any[]) => {
      const { data } = await save({ variables: { courseId, modules } });
      const payload = data?.setCourseProgram;
      return {
        success: Boolean(payload?.success),
        lessonsRemoved: payload?.lessonsRemoved ?? 0,
        lessonsWithoutMarketing: payload?.lessonsWithoutMarketing ?? 0,
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [save],
  );

  return { setProgram, pending: loading };
};
